<template>
    <form class="pl-terminal" novalidate @submit.prevent>
        <div class="pl-terminal__bar">
            <i class="fas fa-terminal" aria-hidden="true"></i>
            <span>{{ t('player.creation.identity.bar') }}</span>
        </div>

        <div class="pl-terminal__body">
            <h1 ref="heading" tabindex="-1" class="pl-visually-hidden">
                {{ t('player.creation.identity.title') }}
            </h1>

            <div class="pl-terminal__row">
                <label class="pl-terminal__label" :for="ids.name">
                    {{ t('player.creation.identity.name') }}
                    <span class="pl-terminal__req" :title="t('player.creation.identity.requiredMark')">*</span>
                </label>
                <input
                    :id="ids.name"
                    class="pl-input"
                    type="text"
                    autocomplete="off"
                    required
                    :value="name"
                    :placeholder="t('player.creation.identity.namePlaceholder')"
                    :aria-invalid="!!nameError"
                    @input="$emit('update:name', $event.target.value)"
                    @blur="touched.name = true"
                />
                <p v-if="nameError" class="pl-field__error">{{ nameError }}</p>
            </div>

            <div class="pl-terminal__row">
                <label class="pl-terminal__label" :for="ids.age">
                    {{ t('player.creation.identity.age') }}
                    <span class="pl-terminal__req" :title="t('player.creation.identity.requiredMark')">*</span>
                </label>
                <input
                    :id="ids.age"
                    class="pl-input"
                    type="number"
                    :min="MIN_AGE"
                    :max="MAX_AGE"
                    inputmode="numeric"
                    required
                    :value="age"
                    :placeholder="t('player.creation.identity.agePlaceholder', { min: MIN_AGE, max: MAX_AGE })"
                    :aria-invalid="!!ageError"
                    @input="$emit('update:age', toAge($event.target.value))"
                    @blur="touched.age = true"
                />
                <p v-if="ageError" class="pl-field__error">{{ ageError }}</p>
            </div>

            <div class="pl-terminal__row">
                <label class="pl-terminal__label" :for="ids.gender">
                    {{ t('player.creation.identity.gender') }}
                </label>
                <select
                    :id="ids.gender"
                    class="pl-select"
                    :value="gender"
                    @change="$emit('update:gender', $event.target.value)"
                >
                    <option v-for="option in GENDERS" :key="option" :value="option">
                        {{ t(`player.creation.identity.genders.${option.toLowerCase()}`) }}
                    </option>
                </select>
            </div>

            <TerminalProse
                :id="ids.appearance"
                :label="t('player.creation.identity.appearance')"
                :placeholder="t('player.creation.identity.appearancePlaceholder')"
                :model-value="appearance"
                :max="MAX_PROSE"
                @update:model-value="$emit('update:appearance', $event)"
            />

            <TerminalProse
                :id="ids.background"
                :label="t('player.creation.identity.background')"
                :placeholder="t('player.creation.identity.backgroundPlaceholder')"
                :model-value="background"
                :max="MAX_PROSE"
                @update:model-value="$emit('update:background', $event)"
            />
        </div>
    </form>
</template>

<script setup>
import {computed, nextTick, onMounted, reactive, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import TerminalProse from '@/components/Player/Creation/TerminalProse.vue';
import {hasUnusableName} from '@/components/Player/characterPresentation.js';

/* Mirrors CharacterBioDraftSerializer (apps/character/api/serializers/openapi.py).
   Stated here so the field rejects what the API would reject, at the moment the
   player types it rather than at submit. */
const MIN_AGE = 18;
const MAX_AGE = 900;
const MAX_PROSE = 5000;
const GENDERS = ['Male', 'Female', 'Other'];

let uidSeq = 0;
function nextUid() {
    uidSeq += 1;
    return uidSeq;
}

const props = defineProps({
    name: {type: String, default: ''},
    age: {type: Number, default: null},
    gender: {type: String, default: 'Male'},
    appearance: {type: String, default: ''},
    background: {type: String, default: ''},
});

defineEmits(['update:name', 'update:age', 'update:gender', 'update:appearance', 'update:background']);

const {t} = useI18n();
const heading = ref(null);

/* A per-instance suffix so two terminals on one page cannot share label
   associations. A counter rather than a random or a timestamp: these are DOM
   ids, and a stable sequence is easier to assert against in a test. */
const uid = nextUid();
const ids = {
    name: `pl-id-name-${uid}`,
    age: `pl-id-age-${uid}`,
    gender: `pl-id-gender-${uid}`,
    appearance: `pl-id-appearance-${uid}`,
    background: `pl-id-background-${uid}`,
};

/* Both of these gate the step's Next button (CharacterSubmit's `canAdvance`),
   and before this they gated it silently: an empty age disabled Next while
   showing nothing at all, and a name of "string" or "Unnamed…" was rejected
   with no message on any input. A disabled control that will not say what it
   wants is the whole defect, so every branch that blocks Next now returns text.

   "Not filled in yet" waits for blur — nagging someone about a field they have
   not reached is noise. A value that is present but wrong is said immediately. */
const touched = reactive({name: false, age: false});

const nameError = computed(() => {
    if (!touched.name) return '';
    const text = String(props.name ?? '').trim();
    if (!text) return t('player.creation.identity.required');
    return hasUnusableName(text) ? t('player.creation.identity.nameRule') : '';
});

const ageError = computed(() => {
    const value = props.age;
    const empty = value === null || value === undefined || value === '';
    if (!empty && (value < MIN_AGE || value > MAX_AGE)) {
        return t('player.creation.identity.ageRange', {min: MIN_AGE, max: MAX_AGE});
    }
    return touched.age && empty ? t('player.creation.identity.required') : '';
});

function toAge(raw) {
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
}

onMounted(async () => {
    await nextTick();
    heading.value?.focus();
});
</script>
